import React from "react";
import { NavLink, Outlet } from "react-router-dom";
import { Leaf, LogOut, ChevronsUpDown, UserCog, BookOpen } from "lucide-react";
import { NAV_ITEMS } from "./nav";
import { useI18n } from "./i18n";
import { useAuth } from "./auth";
import LangToggle from "./components/LangToggle";
import StandardsBadges from "./components/StandardsBadges";

export default function DashboardLayout() {
  const { t, lang } = useI18n();
  const { user, clients, clientId, selectClient, logout } = useAuth();

  const isConsultant = user?.role === "consultant";
  const items = NAV_ITEMS.filter((i) => !i.consultantOnly || isConsultant);

  // Display-only translation for well-known seeded client names so the
  // consultant client switcher reads naturally in the current UI language.
  const displayClientName = (raw) => {
    if (!raw) return raw;
    const map = {
      "Demo Müşteri": { tr: "Demo Müşteri", en: "Demo Client" },
      "Örnek Müşteri": { tr: "Örnek Müşteri", en: "Sample Client" },
      "Kuruluşum": { tr: "Kuruluşum", en: "My Organization" },
    };
    return (map[raw] && map[raw][lang]) || raw;
  };

  return (
    <div className="dash-scope flex min-h-screen bg-[#F5F6F1] text-[#1b2420]" data-testid="dashboard-root">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 flex w-64 flex-col border-r border-[#e4e6dd] bg-white" data-testid="sidebar">
        <div className="flex items-center gap-2 px-5 py-5">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#14512f] text-white"><Leaf className="h-5 w-5" /></span>
          <div className="leading-tight">
            <div className="font-display text-lg text-[#14512f]">{t("brand")}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#8a8f83]">Enasuspro®</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-6">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink key={it.key} to={it.to} end={it.to === "/"} data-testid={`nav-${it.key}`}
                className={({ isActive }) =>
                  `mb-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                    isActive ? "bg-[#14512f] text-white" : "text-[#4b544c] hover:bg-[#eef1e9]"
                  }`}>
                <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
                {t(it.key)}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-[#e4e6dd] p-3">
          <div className="mb-2 px-2 text-xs text-[#8a8f83]" data-testid="current-user">{user?.name || user?.email}</div>
          <NavLink to="/profile" data-testid="nav-profile"
            className={({ isActive }) =>
              `mb-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                isActive ? "bg-[#14512f] text-white" : "text-[#4b544c] hover:bg-[#eef1e9]"
              }`}>
            <UserCog className="h-[18px] w-[18px]" strokeWidth={1.8} /> {t("profileMenu")}
          </NavLink>
          <button onClick={logout} data-testid="logout-btn"
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-[#4b544c] transition-colors hover:bg-[#eef1e9]">
            <LogOut className="h-[18px] w-[18px]" strokeWidth={1.8} /> {t("logoutLabel")}
          </button>
          <a href="/docs" target="_blank" rel="noopener noreferrer" data-testid="nav-docs"
            className="mt-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-[#4b544c] transition-colors hover:bg-[#eef1e9]">
            <BookOpen className="h-[18px] w-[18px]" strokeWidth={1.8} /> {lang === "en" ? "Docs" : "Dokümanlar"}
          </a>
          <div className="mt-3 border-t border-[#e4e6dd] pt-3">
            <div className="mb-1.5 px-2 text-[10px] font-medium uppercase tracking-widest text-[#8a8f83]">
              {lang === "en" ? "Aligned with" : "Uyumlu olduğu standartlar"}
            </div>
            <div className="px-2">
              <StandardsBadges />
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="ml-64 flex min-h-screen flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-4 border-b border-[#e4e6dd] bg-white/80 px-8 py-4 backdrop-blur">
          <p className="hidden text-sm text-[#6b7268] md:block" data-testid="header-tagline">{t("tagline")}</p>
          <div className="flex items-center gap-3">
            {isConsultant && (
              <div className="relative" data-testid="client-switcher">
                <ChevronsUpDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8a8f83]" />
                <select
                  value={clientId}
                  onChange={(e) => selectClient(e.target.value)}
                  data-testid="client-select"
                  className="appearance-none rounded-lg border border-[#dcdfd4] bg-white py-2 pl-3 pr-9 text-sm text-[#1b2420] outline-none focus:border-[#14512f]"
                >
                  {clients.map((c) => <option key={c.id} value={c.id}>{displayClientName(c.name)}</option>)}
                </select>
              </div>
            )}
            <div className="flex overflow-hidden rounded-md" data-testid="header-lang">
              <LangToggle />
            </div>
          </div>
        </header>

        <main className="flex-1 px-8 py-8" key={clientId} data-testid="dashboard-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
